import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CCard, CCardBody } from '@coreui/react-pro'
import AnalyticsV2Service from 'src/service/AnalyticsV2Service'
import FilterBar, { MAX_RANGE_DAYS, daysBetween, rangeForPreset } from './FilterBar'
import OverviewTab from './OverviewTab'
import ScreensTab from './ScreensTab'
import AdsSponsorsTab from './AdsSponsorsTab'
import './AppAnalytics.css'

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'screens', label: 'App Screens' },
  { key: 'ads', label: 'Promos & Sponsors' },
]

const DEFAULT_PRESET = '28d'

/**
 * Filters live in the URL query string, so a filtered view can be bookmarked or
 * pasted to someone else and comes back identical.
 */
const readFilters = (search) => {
  const params = new URLSearchParams(search)
  const preset = params.get('preset') || DEFAULT_PRESET

  let from = params.get('from')
  let to = params.get('to')

  if (preset !== 'custom' || !from || !to) {
    const range = rangeForPreset(preset === 'custom' ? DEFAULT_PRESET : preset)
    if (range) {
      from = range.from
      to = range.to
    }
  }

  return {
    preset,
    from,
    to,
    platform: params.get('platform') || 'all',
    appVersion: params.get('app_version') || 'all',
    tab: params.get('tab') || 'overview',
    interval: params.get('interval') || 'day',
  }
}

const writeFilters = (filters) => {
  const params = new URLSearchParams()
  params.set('preset', filters.preset)
  if (filters.preset === 'custom') {
    if (filters.from) params.set('from', filters.from)
    if (filters.to) params.set('to', filters.to)
  }
  if (filters.platform && filters.platform !== 'all') params.set('platform', filters.platform)
  if (filters.appVersion && filters.appVersion !== 'all') {
    params.set('app_version', filters.appVersion)
  }
  if (filters.tab && filters.tab !== 'overview') params.set('tab', filters.tab)
  if (filters.interval && filters.interval !== 'day') params.set('interval', filters.interval)
  return params.toString()
}

const AppAnalytics = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const [filters, setFilters] = useState(() => readFilters(location.search))

  const [summary, setSummary] = useState(null)
  const [seriesByMetric, setSeriesByMetric] = useState({})
  const [platforms, setPlatforms] = useState(null)
  const [versions, setVersions] = useState(null)
  const [screens, setScreens] = useState(null)
  const [engagement, setEngagement] = useState(null)
  const [adsSponsors, setAdsSponsors] = useState(null)
  const [meta, setMeta] = useState(null)

  const [loading, setLoading] = useState({
    summary: true,
    timeseries: true,
    platforms: true,
    versions: true,
    screens: true,
    engagement: true,
    adsSponsors: true,
  })
  const [errors, setErrors] = useState({})
  const [reloadToken, setReloadToken] = useState(0)

  // The API rejects anything over 92 days; say so before the request is made.
  const spanDays = daysBetween(filters.from, filters.to)
  const rangeTooLong = spanDays > MAX_RANGE_DAYS
  const rangeInvalid = !filters.from || !filters.to || spanDays < 1

  const updateFilters = useCallback(
    (next) => {
      setFilters(next)
      navigate({ search: writeFilters(next) }, { replace: true })
    },
    [navigate],
  )

  // A stable identity for the request params, so effects do not re-fire on every
  // unrelated state change.
  const requestKey = useMemo(
    () =>
      [
        filters.from,
        filters.to,
        filters.platform,
        filters.appVersion,
        filters.interval,
        reloadToken,
      ].join('|'),
    [filters.from, filters.to, filters.platform, filters.appVersion, filters.interval, reloadToken],
  )

  const apiFilters = useMemo(
    () => ({
      from: filters.from,
      to: filters.to,
      platform: filters.platform,
      appVersion: filters.appVersion,
    }),
    [filters.from, filters.to, filters.platform, filters.appVersion],
  )

  useEffect(() => {
    if (rangeTooLong || rangeInvalid) {
      setLoading({
        summary: false,
        timeseries: false,
        platforms: false,
        versions: false,
        screens: false,
        engagement: false,
        adsSponsors: false,
      })
      return undefined
    }

    let cancelled = false

    setLoading({
      summary: true,
      timeseries: true,
      platforms: true,
      versions: true,
      screens: true,
      engagement: true,
      adsSponsors: true,
    })
    setErrors({})

    const done = (key) => {
      if (!cancelled) setLoading((prev) => ({ ...prev, [key]: false }))
    }
    const fail = (key, err) => {
      if (!cancelled) {
        setErrors((prev) => ({ ...prev, [key]: err?.message || 'Request failed.' }))
      }
    }
    // A failed request must never look like "zero traffic" — the tile shows an
    // error state instead of a confident zero.
    const handle = (key, setter) => (res) => {
      if (cancelled) return
      if (res.success) {
        setter(res.data)
        if (res.meta) setMeta(res.meta)
      } else {
        fail(key, res)
      }
    }

    AnalyticsV2Service.getSummary(apiFilters)
      .then(handle('summary', setSummary))
      .catch((err) => fail('summary', err))
      .finally(() => done('summary'))

    // Three series: the sessions one drives the trend chart, and all three feed
    // the KPI sparklines.
    Promise.all(
      ['sessions', 'screen_views', 'active_users'].map((metric) =>
        AnalyticsV2Service.getTimeseries(apiFilters, metric, filters.interval).then((res) => ({
          metric,
          res,
        })),
      ),
    )
      .then((results) => {
        if (cancelled) return
        const next = {}
        results.forEach(({ metric, res }) => {
          if (res.success) {
            next[metric] = res.data.current || []
            if (res.meta) setMeta(res.meta)
          } else {
            fail('timeseries', res)
          }
        })
        setSeriesByMetric(next)
      })
      .catch((err) => fail('timeseries', err))
      .finally(() => done('timeseries'))

    AnalyticsV2Service.getPlatforms(apiFilters)
      .then(handle('platforms', (data) => setPlatforms(data.current || [])))
      .catch((err) => fail('platforms', err))
      .finally(() => done('platforms'))

    AnalyticsV2Service.getAppVersions(apiFilters)
      .then(handle('versions', (data) => setVersions(data.current || [])))
      .catch((err) => fail('versions', err))
      .finally(() => done('versions'))

    AnalyticsV2Service.getTopScreens(apiFilters)
      .then(handle('screens', setScreens))
      .catch((err) => fail('screens', err))
      .finally(() => done('screens'))

    AnalyticsV2Service.getEngagement(apiFilters)
      .then(handle('engagement', setEngagement))
      .catch((err) => fail('engagement', err))
      .finally(() => done('engagement'))

    AnalyticsV2Service.getAdsSponsors(apiFilters)
      .then(handle('adsSponsors', setAdsSponsors))
      .catch((err) => fail('adsSponsors', err))
      .finally(() => done('adsSponsors'))

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, rangeTooLong, rangeInvalid])

  const rangeError = rangeInvalid
    ? 'Choose a start date on or before the end date.'
    : rangeTooLong
    ? `Please choose a range of ${MAX_RANGE_DAYS} days or fewer. You have selected ${spanDays} days.`
    : null

  const setTab = (tab) => updateFilters({ ...filters, tab })

  const retry = () => setReloadToken((t) => t + 1)

  // Per-tab counts for the .ge-tab-count pills. null hides the pill while the
  // number is still loading, rather than flashing a misleading 0.
  const tabCounts = {
    overview: null,
    screens: loading.screens ? null : screens?.total ?? null,
    ads: loading.adsSponsors ? null : adsSponsors?.rows?.length ?? null,
  }

  return (
    <div className="av2-page">
      <div className="ge-page-header">
        <h1 className="ge-page-title">App Analytics</h1>
        <p className="ge-page-subtitle">How members use the mobile app, on iPhone and Android.</p>
      </div>

      <FilterBar
        filters={filters}
        onChange={updateFilters}
        appVersions={versions}
        meta={meta}
        rangeError={rangeError}
      />

      <div className="ge-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={filters.tab === tab.key}
            className={`ge-tab${filters.tab === tab.key ? ' active' : ''}`}
            onClick={() => setTab(tab.key)}
          >
            {tab.label}
            {tabCounts[tab.key] !== null && (
              <span className="ge-tab-count">{tabCounts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {rangeError ? (
        <CCard>
          <CCardBody className="text-center py-5">
            <div className="fw-semibold mb-1">Adjust the date range to see your data</div>
            <div className="text-medium-emphasis small">{rangeError}</div>
          </CCardBody>
        </CCard>
      ) : (
        // Only the active tab is mounted. Chart.js measures its canvas parent on
        // init, so a chart created inside a hidden (display:none) pane comes back
        // 0-sized and then paints outside its card when the pane is shown. Keeping
        // one tab mounted at a time is what makes the charts size correctly.
        <>
          {filters.tab === 'overview' && (
            <OverviewTab
              summary={summary}
              timeseries={seriesByMetric.sessions}
              seriesByMetric={seriesByMetric}
              platforms={platforms}
              versions={versions}
              engagement={engagement}
              interval={filters.interval}
              loading={loading}
              errors={errors}
              onRetry={retry}
            />
          )}

          {filters.tab === 'screens' && (
            <ScreensTab
              screens={screens}
              filters={apiFilters}
              loading={loading.screens}
              error={errors.screens}
              onRetry={retry}
            />
          )}

          {filters.tab === 'ads' && (
            <AdsSponsorsTab
              data={adsSponsors}
              loading={loading.adsSponsors}
              error={errors.adsSponsors}
              onRetry={retry}
            />
          )}
        </>
      )}
    </div>
  )
}

export default AppAnalytics
